import React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  MessageCircle, 
  Send, 
  Sparkles,
  Clock,
  Users,
  ArrowRight,
  CheckCircle2
} from "lucide-react";
import { PageNavigation, PageBreadcrumb } from "@/components/PageNavigation";

// WhatsApp icon component
const WhatsAppIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Telegram icon component
const TelegramIcon = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

export const GetStartedPage = () => {
  const { t } = useTranslation();

  const benefits = [
    t('getStarted.benefits.benefit1'),
    t('getStarted.benefits.benefit2'),
    t('getStarted.benefits.benefit3'),
    t('getStarted.benefits.benefit4'),
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Top Breadcrumb Navigation */}
      <PageBreadcrumb currentPath="/get-started" />

      {/* Hero Section */}
      <div className="w-full py-12 md:py-16 px-4 bg-gradient-to-b from-primary/5 via-primary/3 to-background">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-primary" />
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              {t('getStarted.startYourJourney')}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t('getStarted.title')}<br />
            <span className="text-primary">{t('getStarted.titleHighlight')}</span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('getStarted.subtitle')}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16 -mt-4">
        {/* Benefits */}
        <div className="grid sm:grid-cols-2 gap-3 mb-10">
          {benefits.map((benefit, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm text-foreground">{benefit}</span>
            </div>
          ))}
        </div>

        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {/* WhatsApp Card */}
          <Card className="overflow-hidden border-2 border-transparent hover:border-[#25D366]/30 transition-all duration-300 hover:shadow-lg group">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-[#25D366]/10 group-hover:bg-[#25D366]/20 transition-colors">
                  <WhatsAppIcon className="w-7 h-7 text-[#25D366]" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">{t('getStarted.whatsapp')}</h3>
                  <p className="text-sm text-muted-foreground">{t('getStarted.whatsappDesc')}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {t('getStarted.whatsappText')}
              </p>
              <a 
                href="https://wa.me/18182780428" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2">
                  <MessageCircle className="w-4 h-4" />
                  {t('getStarted.chatOnWhatsapp')}
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Telegram Card */}
          <Card className="overflow-hidden border-2 border-transparent hover:border-[#0088cc]/30 transition-all duration-300 hover:shadow-lg group">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-xl bg-[#0088cc]/10 group-hover:bg-[#0088cc]/20 transition-colors">
                  <TelegramIcon className="w-7 h-7 text-[#0088cc]" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-foreground">{t('getStarted.telegram')}</h3>
                  <p className="text-sm text-muted-foreground">{t('getStarted.telegramDesc')}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {t('getStarted.telegramText')}
              </p>
              <a 
                href="https://t.me/lumonpeptides" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="w-full bg-[#0088cc] hover:bg-[#0077b5] text-white gap-2">
                  <Send className="w-4 h-4" />
                  {t('getStarted.messageOnTelegram')}
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>

        {/* Support Hours */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
              <div className="p-3 rounded-full bg-primary/10">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                  <Users className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold text-foreground">{t('getStarted.dedicatedSupport')}</h3>
                </div>
                <p className="text-muted-foreground">
                  {t('getStarted.availableText')} <span className="font-semibold text-primary">{t('getStarted.sevenDays')}</span> {t('getStarted.toAssist')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-10 text-center">
          <p className="text-sm text-muted-foreground">
            {t('getStarted.specialistReach')}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {t('getStarted.privacyNote')}
          </p>
        </div>
      </div>

      {/* Page Navigation */}
      <PageNavigation currentPath="/get-started" />

      {/* Footer Disclaimer */}
      <div className="border-t border-border/50 bg-muted/20 py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            <strong>{t('common.disclaimer')}</strong> {t('common.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default GetStartedPage;
